import React, { useState, useCallback, useMemo } from 'react'
import { Modal } from 'components/UI/Modal/Modal'
import apiService from 'services/api'
import { Variant, Product } from 'models/product'
import { Form } from 'react-bootstrap'
import { FaSearch } from 'react-icons/fa'
import DataTable from '../DataTable/DataTable/DataTable'
import { ColumnDef } from '@tanstack/react-table'
import Select from 'components/FormUtils/Select'
import { useForm } from 'react-hook-form'
import { DataTableProvider } from 'components/DataTable'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'

interface ProductVariantSearchModalProps {
  open: boolean
  onClose: () => void
  onSelectVariant: (variant: Variant, product: Product) => void
}

// Define the table data type
interface VariantTableData {
  id: string
  product: Product
  variant: Variant
  productName: string
  variantName: string
  sku: string
  gtin: string
  formatPackSize: string
}

export function ProductVariantSearchModal({
  open,
  onClose,
  onSelectVariant,
}: ProductVariantSearchModalProps) {
  const api = apiService()
  const { data: products, isLoading } = api.useGetProducts()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedVariant, setSelectedVariant] = useState<{
    variant: Variant
    product: Product
  } | null>(null)

  // Form for the product filter
  const { control, watch } = useForm({
    defaultValues: {
      productFilter: null as number | null,
    },
  })

  const selectedProductId = watch('productFilter')

  // Create flat list of all variants for the table
  const allVariants = useMemo(() => {
    if (!products) return []

    const variants: VariantTableData[] = []
    products.forEach((product) => {
      product.variants?.forEach((variant) => {
        variants.push({
          id: `${product.id}-${variant.id}`,
          product,
          variant,
          productName: product.name,
          variantName: variant.name || '',
          sku: variant.sku || '',
          gtin: variant.gtin || '',
          formatPackSize: variant.format_pack_size || '',
        })
      })
    })
    return variants
  }, [products])

  // Filter variants based on search term and product selection
  const filteredVariants = useMemo(() => {
    let filtered = allVariants

    // Filter by selected product
    if (selectedProductId) {
      filtered = filtered.filter(
        (item) => item.product.id === selectedProductId
      )
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim()
      filtered = filtered.filter((item) => {
        return (
          item.productName.toLowerCase().includes(term) ||
          item.variantName.toLowerCase().includes(term) ||
          item.sku.toLowerCase().includes(term) ||
          item.gtin.toLowerCase().includes(term) ||
          item.formatPackSize.toLowerCase().includes(term)
        )
      })
    }

    return filtered
  }, [allVariants, selectedProductId, searchTerm])

  const handleVariantClick = useCallback(
    (variant: Variant, product: Product) => {
      setSelectedVariant({ variant, product })
    },
    []
  )

  const handleSelectClick = useCallback(() => {
    if (selectedVariant) {
      onSelectVariant(selectedVariant.variant, selectedVariant.product)
      setSelectedVariant(null)
      setSearchTerm('')
      onClose()
    }
  }, [selectedVariant, onSelectVariant, onClose])

  // Product options for dropdown
  const productOptions = useMemo(() => {
    if (!products) return []
    return [
      { label: 'All Products', value: null },
      ...products.map((product) => ({
        label: product.name,
        value: product.id,
      })),
    ]
  }, [products])

  const handleClose = useCallback(() => {
    setSelectedVariant(null)
    setSearchTerm('')
    onClose()
  }, [onClose])

  // Define table columns
  const columns: ColumnDef<VariantTableData>[] = useMemo(
    () => [
      {
        id: 'product',
        header: 'Product',
        accessorKey: 'productName',
        size: 200,
      },
      {
        id: 'variant',
        header: 'Variant',
        accessorKey: 'variantName',
        size: 200,
        cell: ({ row }) => {
          const item = row.original
          return (
            <div>
              <div className="font-medium mb-1">{item.variantName}</div>
              {item.formatPackSize && (
                <div className="text-sm text-gray-600">
                  {item.formatPackSize}
                </div>
              )}
            </div>
          )
        },
      },
      {
        id: 'sku',
        header: 'SKU',
        accessorKey: 'sku',
        size: 120,
      },
      {
        id: 'gtin',
        header: 'GTIN',
        accessorKey: 'gtin',
        size: 120,
      },
    ],
    [selectedVariant, handleVariantClick]
  )

  return (
    <Modal
      open={open}
      onOpenChange={(open) => !open && handleClose()}
      size="xl"
      title="Select Product Variant"
      onAccept={handleSelectClick}
      acceptButtonText="Select Variant"
      blockAccept={!selectedVariant}
      onCancel={handleClose}
      cancelButtonText="Cancel"
      loading={isLoading}
    >
      <div>
        {/* Filters */}
        <div className="flex gap-4 mb-4 items-end">
          <div className="flex-1 relative">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 z-10" />
              <Form.Control
                type="text"
                placeholder="Search by product name, variant name, SKU, GTIN..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                className="h-[38px] pl-10"
              />
            </div>
          </div>

          <div className="min-w-[200px]">
            <Select
              control={control}
              name="productFilter"
              options={productOptions}
              placeholder="All Products"
              isMulti={false}
              isClearable
            />
          </div>
        </div>

        {/* Selected Variant Display */}
        {selectedVariant && (
          <div className="inline-block bg-blue-50 border border-blue-400 rounded p-2 mb-4 text-sm text-blue-900">
            Selected: <strong>{selectedVariant.product.name}</strong> -{' '}
            {selectedVariant.variant.name}
            {selectedVariant.variant.format_pack_size && (
              <span> ({selectedVariant.variant.format_pack_size})</span>
            )}
          </div>
        )}

        {/* Data Table */}
        <div className="[&_.selected-row]:bg-blue-50 [&_.clickable-row]:cursor-pointer [&_.clickable-row:hover]:bg-gray-50 [&_.selected-row:hover]:bg-blue-100">
          <ColumnsStoreProvider
            tableKey="product-variant-search"
            savedPresetsIdentifier={'PRODUCT_VARIANT_SEARCH' as any}
          >
            <DataTableProvider tableKey="product-variant-search">
              <DataTable
                columns={columns}
                data={filteredVariants}
                height={400}
                loading={isLoading}
                sortableFields="all"
                tableKey="product-variant-search"
                onRowClick={(row) => {
                  const item = row.original
                  handleVariantClick(item.variant, item.product)
                }}
                rowAttributes={(row) => {
                  const item = row.original
                  const isSelected =
                    selectedVariant?.variant.id === item.variant.id &&
                    selectedVariant?.product.id === item.product.id

                  return {
                    className: `clickable-row ${isSelected ? 'selected-row' : ''}`,
                  }
                }}
                emptyTableText={{
                  title:
                    searchTerm || selectedProductId
                      ? 'No variants match your search criteria'
                      : 'No variants available',
                  subtitle: 'Try adjusting your filters or search terms',
                }}
              />
            </DataTableProvider>
          </ColumnsStoreProvider>
        </div>
      </div>
    </Modal>
  )
}
